import { FC, useState } from 'react';

import { BulkAutoPriceProgress } from './BulkAutoPriceProgress';
import {
    BulkAutoPriceControls,
    BulkAutoPriceSettings,
} from './BulkAutoPriceSettings';
import { Input } from '../../../../components/ui/Input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/Tabs';
import { OfferProperty } from '../../../../types';
import { MAX_STRING_PROPERTY_LENGTH } from '../__shared/offerDraft';

export type { BulkAutoPriceControls } from './BulkAutoPriceSettings';

interface Props {
    properties: OfferProperty[];
    selectedCount: number;
    totalCount: number;
    autoPrice: BulkAutoPriceControls | null;
    onPropertyApply: (slug: string, value: string) => void;
    onManualPriceChange: (price: string) => void;
    onPriceApply: (price: string) => void;
}

export const BulkOfferMassEdit: FC<Props> = ({
    properties,
    selectedCount,
    totalCount,
    autoPrice,
    onPropertyApply,
    onManualPriceChange,
    onPriceApply,
}) => {
    const [manualPrice, setManualPrice] = useState('');
    const [propertyValues, setPropertyValues] = useState<
        Record<string, string>
    >({});

    const disabled = totalCount === 0;
    const targetLabel =
        selectedCount > 0
            ? `до обраних (${selectedCount})`
            : `до всіх товарів (${totalCount})`;
    const priceMode = autoPrice?.enabled ? 'auto' : 'manual';

    const handleManualPriceChange = (price: string) => {
        setManualPrice(price);
        onManualPriceChange(price.trim());

        if (price.trim()) {
            onPriceApply(price.trim());
        }
    };

    const handlePropertyChange = (slug: string, value: string) => {
        setPropertyValues((currentValues) => ({
            ...currentValues,
            [slug]: value,
        }));
        onPropertyApply(slug, value);
    };

    return (
        <div
            className="border-t bg-muted/30"
            data-testid="bulk-offer-mass-edit"
        >
            <div className="flex w-full items-center gap-2 px-3 py-2.5 text-left sm:px-4">
                <span className="text-sm font-medium">Масові зміни</span>
                <span className="truncate text-xs text-muted-foreground">
                    {targetLabel}
                </span>
                {autoPrice?.enabled && (
                    <BulkAutoPriceProgress
                        progress={autoPrice.progress}
                        onRetryFailed={autoPrice.onRetryFailed}
                    />
                )}
            </div>

            <div
                id="bulk-offer-mass-edit-content"
                className="flex flex-wrap items-center gap-x-4 gap-y-3 px-3 pb-3 sm:px-4"
            >
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">Ціна:</span>
                    {autoPrice && (
                        <Tabs
                            value={priceMode}
                            onValueChange={(value) =>
                                autoPrice.onToggle(value === 'auto')
                            }
                        >
                            <TabsList className="grid h-9 grid-cols-2 p-0.5">
                                <TabsTrigger
                                    value="manual"
                                    className="h-8 px-2.5 text-sm"
                                >
                                    Вручну
                                </TabsTrigger>
                                <TabsTrigger
                                    value="auto"
                                    className="h-8 px-2.5 text-sm"
                                >
                                    Автоціни
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    )}

                    {priceMode === 'auto' && autoPrice ? (
                        <BulkAutoPriceSettings controls={autoPrice} />
                    ) : (
                        <>
                            <div className="relative">
                                <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    aria-label="Ціна для масової зміни"
                                    placeholder="0"
                                    value={manualPrice}
                                    className="h-9 w-28 pr-7 text-sm"
                                    onChange={(event) =>
                                        handleManualPriceChange(
                                            event.target.value,
                                        )
                                    }
                                />
                                <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sm text-muted-foreground">
                                    ₴
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {properties.map((property) => (
                    <div
                        key={property.slug}
                        className="flex items-center gap-2"
                    >
                        <label
                            htmlFor={`bulk-edit-${property.slug}`}
                            className="text-sm text-muted-foreground"
                        >
                            {property.name}:
                        </label>
                        {property.kind === 'collection' ? (
                            <Select
                                value={propertyValues[property.slug] ?? ''}
                                disabled={disabled}
                                onValueChange={(value) =>
                                    handlePropertyChange(property.slug, value)
                                }
                            >
                                <SelectTrigger
                                    id={`bulk-edit-${property.slug}`}
                                    aria-label={`${property.name} для масової зміни`}
                                    className="h-9 w-[132px] text-sm"
                                >
                                    <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                    {property.offer_property_options.map(
                                        (option) => (
                                            <SelectItem
                                                key={option.slug}
                                                value={option.slug}
                                            >
                                                {option.name}
                                            </SelectItem>
                                        ),
                                    )}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                id={`bulk-edit-${property.slug}`}
                                type="text"
                                maxLength={MAX_STRING_PROPERTY_LENGTH}
                                aria-label={`${property.name} для масової зміни`}
                                placeholder={property.name}
                                disabled={disabled}
                                value={propertyValues[property.slug] ?? ''}
                                className="h-9 w-[180px] text-sm"
                                onChange={(event) =>
                                    handlePropertyChange(
                                        property.slug,
                                        event.target.value,
                                    )
                                }
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
